import { useEffect } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { Toaster } from 'sonner'
import { Loader2 } from 'lucide-react'
import { useAuth } from '@/store/auth'
import { tickSocket } from '@/feed/tickSocket'
import { useLeaveGuard } from '@/hooks/useLeaveGuard'
import { accountStore } from '@/store/account'
import { TerminalShell } from '@/components/TerminalShell'
import Login from '@/screens/Login'
import Terminal from '@/screens/Terminal'
import Accounts from '@/screens/Accounts'
import Strategies from '@/screens/Strategies'
import Portfolio from '@/screens/Portfolio'
import Journal from '@/screens/Journal'
import NotBuilt from '@/screens/NotBuilt'
import Simulation from '@/screens/Simulation'
import Traders from '@/screens/Traders'

/**
 * Route guard.
 *
 * `booting` is a distinct state from `anonymous` on purpose: the session lives
 * in an httpOnly cookie, so on a reload we do not yet know whether the user is
 * signed in. Treating "don't know" as "signed out" would bounce every reload
 * to the login screen and back — a flash that is merely ugly on a dashboard
 * and genuinely alarming on a screen holding positions.
 */
function Protected({ children }: { children: React.ReactNode }) {
  const status = useAuth((s) => s.status)

  /*
   * The tick socket lives as long as the signed-in session, not as long as a
   * screen. Opening it per screen would mean a reconnect on every navigation,
   * and each reconnect re-sends every subscription.
   *
   * It needs the access token, which only exists once auth has settled — so
   * it is started here rather than at module load.
   */
  useEffect(() => {
    if (status !== 'authenticated') return
    tickSocket.connect()
    return () => tickSocket.disconnect()
  }, [status])

  /*
   * The broker accounts, for the same reason and in the same place.
   *
   * They belong to the SESSION, not to a screen. Loading them in the Terminal
   * meant a cold reload of any other route found an empty store: the top
   * bar's account selector rendered nothing, and the Portfolio had no
   * connection to read a projection for, so it showed a flat account until
   * the Terminal had been visited once.
   */
  useEffect(() => {
    if (status !== 'authenticated') return
    void accountStore.load()
  }, [status])

  /*
   * "LEAVE SITE?" ON EVERY SIGNED-IN SCREEN.
   *
   * Here rather than on a screen, and for the same reason the socket and the
   * account store are here: it belongs to the SESSION. A trading application
   * is not a tab you close by accident, and that is as true of the Portfolio
   * and the Journal as it is of the ladder — the first version guarded only
   * the Terminal, and only while an order was in flight, which armed it for
   * about one round trip in a thousand.
   *
   * Scoped to `authenticated` so the login screen never prompts. Someone who
   * is not signed in has nothing of theirs in the tab, and a dialog there
   * would be noise on the one page people legitimately close.
   *
   * The browser writes the sentence, not us — see `lib/leaveGuard.ts` for
   * that and for the two other constraints that shape this.
   */
  useLeaveGuard('session', status === 'authenticated', 'Signed in to ApnaAlgo')

  if (status === 'booting') {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <Loader2 size={20} className="animate-spin text-muted-foreground" />
      </div>
    )
  }
  if (status === 'anonymous') return <Navigate to="/login" replace />
  return <>{children}</>
}

function AnonymousOnly({ children }: { children: React.ReactNode }) {
  const status = useAuth((s) => s.status)
  if (status === 'authenticated') return <Navigate to="/" replace />
  return <>{children}</>
}

export default function App() {
  const boot = useAuth((s) => s.boot)

  useEffect(() => {
    void boot()
  }, [boot])

  return (
    <BrowserRouter>
      {/*
        The cards draw themselves — see `components/Toast.tsx`. Sonner is
        the stack and the timer only, so `unstyled` keeps its chrome from
        painting a second border around ours.
      */}
      <Toaster position="bottom-right" toastOptions={{ unstyled: true }} />
      <Routes>
        <Route
          path="/login"
          element={
            <AnonymousOnly>
              <Login />
            </AnonymousOnly>
          }
        />

        <Route
          element={
            <Protected>
              <TerminalShell />
            </Protected>
          }
        >
          <Route path="/" element={<Terminal />} />
          <Route path="/accounts" element={<Accounts />} />
          <Route path="/simulation" element={<Simulation />} />
          <Route path="/traders" element={<Traders />} />
          <Route path="/strategies" element={<Strategies />} />
          <Route path="/portfolio" element={<Portfolio />} />
          <Route path="/journal" element={<Journal />} />
          <Route path="/schedule" element={<NotBuilt screen="schedule" />} />
          <Route path="/drafts" element={<NotBuilt screen="drafts" />} />
          <Route path="/learn" element={<NotBuilt screen="learn" />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  )
}
